// web/src/scene/hooks/useBootstrapMap.ts
import { useEffect, useState } from 'react'
import { supabase } from '../../app/supabaseClient'
import { useAppDispatch } from '../../app/useAppDispatch'
import { mapActions } from '@shared/slices/mapSlice'
import type { ViewConfig } from '@shared/types/mapTypes'

interface BootstrapState {
  loading: boolean
  error: string | null
}

// Hardcoded safety-net defaults — match the seeded BoxedMine config so the
// scene boots even if Supabase is unreachable or returns '{}' for view_config.
const DEFAULT_VIEW_CONFIG: ViewConfig = {
  cameraPosition: [9060, 6049, 6000],
  cameraTarget:   [9060, 6049, 4099],
  gridPosition:   [9060, 5594, 4099],
  gridSize:       1500,
  gridDivisions:  100,
}
const DEFAULT_MAP_FILE = 'map_boxed.glb'

// Detect transient JWT clock-skew errors. Supabase Auth occasionally mints
// a token whose `iat` clock is slightly ahead of the REST API's clock, so
// the very first request after sign-in is rejected as "JWT issued at
// future". The skew resolves within ~1–2s as the clocks reconcile, so a
// short backoff retry recovers the bootstrap without forcing a reload.
function isTransientJwtError(message: string | undefined): boolean {
  if (!message) return false
  const m = message.toLowerCase()
  return m.includes('issued at future') || m.includes('jwt expired')
}

const RETRY_DELAYS_MS = [1500, 1500] // up to 2 retries (3 total attempts)

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function useBootstrapMap(): BootstrapState {
  const dispatch = useAppDispatch()
  const [state, setState] = useState<BootstrapState>({ loading: true, error: null })

  useEffect(() => {
    let cancelled = false

    const applyDefaults = () => {
      dispatch(mapActions.setViewConfig(DEFAULT_VIEW_CONFIG))
      dispatch(mapActions.setDefaultMapFile(DEFAULT_MAP_FILE))
      dispatch(mapActions.setMineId(null))
    }

    async function bootstrap() {
      try {
        // Retry loop — only kicks in for transient JWT clock-skew errors.
        // Real errors (network down, RLS denial, missing table) fall
        // through to the warning-toast path on the first try.
        type MineRow = { id: string; view_config: unknown; default_map_file: string | null }
        let data: MineRow | null = null
        let lastError: { message?: string; code?: string } | null = null
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
          const result = await supabase
            .from('mines')
            .select('id, view_config, default_map_file')
            .limit(1)
            .single()
          if (cancelled) return

          if (!result.error && result.data) {
            data = result.data as unknown as MineRow
            lastError = null
            break
          }
          lastError = result.error
          if (!isTransientJwtError(result.error?.message) || attempt >= RETRY_DELAYS_MS.length) {
            break // non-transient error, or out of retries
          }
          console.warn(
            `[useBootstrapMap] Transient JWT error — retrying in ${RETRY_DELAYS_MS[attempt]}ms (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length}).`,
            result.error?.message,
          )
          await sleep(RETRY_DELAYS_MS[attempt])
          if (cancelled) return
        }

        if (!data) {
          console.warn(
            '[useBootstrapMap] Supabase mines fetch failed — using defaults.',
            lastError?.message ?? 'no data',
          )
          applyDefaults()
          setState({ loading: false, error: lastError?.message ?? 'No mine row' })
          return
        }

        // view_config is a JSONB blob and can be '{}' per the SQL default.
        // Merge incoming fields onto the defaults so a partial config is safe.
        const vc = (data.view_config ?? {}) as Partial<ViewConfig>
        const merged: ViewConfig = {
          cameraPosition: vc.cameraPosition ?? DEFAULT_VIEW_CONFIG.cameraPosition,
          cameraTarget:   vc.cameraTarget   ?? DEFAULT_VIEW_CONFIG.cameraTarget,
          gridPosition:   vc.gridPosition   ?? DEFAULT_VIEW_CONFIG.gridPosition,
          gridSize:       vc.gridSize       ?? DEFAULT_VIEW_CONFIG.gridSize,
          gridDivisions:  vc.gridDivisions  ?? DEFAULT_VIEW_CONFIG.gridDivisions,
        }

        dispatch(mapActions.setViewConfig(merged))
        dispatch(mapActions.setDefaultMapFile(data.default_map_file ?? DEFAULT_MAP_FILE))
        dispatch(mapActions.setMineId(data.id))
        setState({ loading: false, error: null })
      } catch (err: unknown) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Unknown bootstrap error'
        console.warn('[useBootstrapMap] bootstrap threw — using defaults:', msg)
        applyDefaults()
        setState({ loading: false, error: msg })
      }
    }

    bootstrap()
    return () => {
      cancelled = true
    }
  }, [dispatch])

  return state
}
